/**
 * /admin/env-setup
 *
 * Checklist de variables de entorno para Railway (web + webhook) y Cloudflare Worker.
 * NO almacena secretos — solo sirve como guía de referencia.
 * SPA Vite + Express (Railway) — no Nuxt/Vercel.
 */

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  AlertTriangle,
  Info,
  Lock,
  Eye,
  Globe,
  Webhook,
  Database,
  CreditCard,
  Landmark,
  Shield,
} from "lucide-react";
import {
  BRAND_SITE_HOST,
} from "@/lib/brandConfig";
import { useState } from "react";

// ── Helpers ───────────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="text-muted-foreground hover:text-foreground transition-colors ml-1"
      title="Copiar"
    >
      {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

type EnvVar = {
  name: string;
  example?: string;
  description: string;
  required: "required" | "recommended" | "optional";
  secret: boolean;
  scope?: string; // "server" | "client" | "both"
};

type Section = {
  title: string;
  icon: React.ReactNode;
  color: string;
  vars: EnvVar[];
  docs?: { label: string; url: string };
  notes?: string[];
};

// ── Sections ──────────────────────────────────────────────────────────────────

const SECTIONS: Section[] = [
  {
    title: "Identidad del Brand",
    icon: <Globe className="h-4 w-4" />,
    color: "text-blue-600",
    docs: { label: "brandConfig.ts", url: "#" },
    notes: [
      "Estas variables personalizan todo el sitio para el nuevo brand.",
      "BRAND_SITE_URL es la URL canónica — usada en redirects de Stripe y MP post-pago.",
    ],
    vars: [
      {
        name: "BRAND_PERSON_NAME",
        example: "Ana Fernández",
        description: "Nombre legal / real de la creadora. Aparece en emails, SEO y UI.",
        required: "required",
        secret: false,
      },
      {
        name: "BRAND_NAME",
        example: "Ana Fernández",
        description: "Nombre público del brand / handle. Puede diferir del nombre legal.",
        required: "required",
        secret: false,
      },
      {
        name: "BRAND_SOURCE_KEY",
        example: "anaafernandez",
        description: "Clave corta sin espacios. Usada en metadata de Stripe/MP, DB records, Mux. Debe coincidir con VITE_BRAND_SOURCE_KEY.",
        required: "required",
        secret: false,
      },
      {
        name: "BRAND_SITE_URL",
        example: "https://www.anaafernandez.com",
        description:
          "URL pública del sitio principal (origen canónico). Crítica para redirects Stripe/Mercado Pago e invitaciones. Debe coincidir con VITE_BRAND_SITE_URL. Ver api/lib/canonicalSiteUrl.ts.",
        required: "required",
        secret: false,
      },
      {
        name: "BRAND_SITE",
        example: "anaafernandez.com",
        description: "Hostname sin protocolo. Usado en emails y footers.",
        required: "required",
        secret: false,
      },
      {
        name: "BRAND_SUPPORT_EMAIL",
        example: "hola@anaafernandez.com",
        description: "Email de soporte visible en la UI.",
        required: "recommended",
        secret: false,
      },
      {
        name: "VITE_BRAND_PERSON_NAME",
        example: "Ana Fernández",
        description: "Igual que BRAND_PERSON_NAME pero expuesto al frontend (prefijo VITE_).",
        required: "required",
        secret: false,
        scope: "client",
      },
      {
        name: "VITE_BRAND_SOURCE_KEY",
        example: "anaafernandez",
        description: "Igual que BRAND_SOURCE_KEY pero expuesto al frontend.",
        required: "required",
        secret: false,
        scope: "client",
      },
      {
        name: "VITE_BRAND_SITE_URL",
        example: "https://www.anaafernandez.com",
        description: "Igual que BRAND_SITE_URL pero expuesto al frontend.",
        required: "required",
        secret: false,
        scope: "client",
      },
      {
        name: "INVITATION_LINK_DESTINATION_BASE_URL",
        example: "https://www.anaafernandez.com",
        description:
          "URL base para links de invitación. Suele coincidir con BRAND_SITE_URL; también entra en la cadena de canonicalSiteUrl si faltan otras.",
        required: "required",
        secret: false,
      },
    ],
  },
  {
    title: "MercadoPago (legacy — no usar en este brand)",
    icon: <Landmark className="h-4 w-4" />,
    color: "text-muted-foreground",
    notes: [
      "Este sitio cobra solo con Stripe (USD), incluida Argentina. No cargues vars de MercadoPago para producción.",
      "Los endpoints /api/mercadopago/* quedan por compatibilidad histórica; no habilitan checkout nuevo.",
    ],
    vars: [
      {
        name: "MERCADOPAGO_ACCESS_TOKEN",
        example: "(omitir)",
        description: "No requerido — pagos nuevos van por Stripe.",
        required: "optional",
        secret: true,
        scope: "server",
      },
    ],
  },
  {
    title: "Stripe",
    icon: <CreditCard className="h-4 w-4" />,
    color: "text-indigo-600",
    docs: { label: "Stripe Dashboard", url: "https://dashboard.stripe.com/apikeys" },
    notes: [
      "La Secret Key se usa en el servidor para crear sesiones de checkout y customer portal.",
      "La Publishable Key se usa en el frontend (Stripe.js) para renderizar el formulario de pago.",
      `Webhook URL a registrar en Stripe: https://${BRAND_SITE_HOST}/api/stripe-webhook`,
    ],
    vars: [
      {
        name: "STRIPE_SECRET_KEY",
        example: "sk_live_...",
        description: "Clave secreta de Stripe. NUNCA exponerla al frontend. Usada en /api/create-subscription-checkout, /api/stripe/create-customer-portal, etc.",
        required: "required",
        secret: true,
        scope: "server",
      },
      {
        name: "VITE_STRIPE_PUBLISHABLE_KEY",
        example: "pk_live_...",
        description: "Clave publicable de Stripe para el frontend. Usada por Stripe Elements y Stripe.js.",
        required: "required",
        secret: false,
        scope: "client",
      },
      {
        name: "STRIPE_WEBHOOK_SECRET",
        example: "whsec_...",
        description: "Secret del webhook de Stripe. Requerido para verificar que los eventos vienen realmente de Stripe.",
        required: "required",
        secret: true,
        scope: "server",
      },
    ],
  },
  {
    title: "Autenticación Admin (Clerk)",
    icon: <Shield className="h-4 w-4" />,
    color: "text-purple-600",
    docs: { label: "Clerk Dashboard", url: "https://dashboard.clerk.com" },
    notes: [
      "Clerk protege las rutas /admin/*. Sin estas vars, el panel admin no funciona.",
      "ADMIN_DASHBOARD_API_KEY es una clave arbitraria que defines tú — úsala en el header Authorization: Bearer <key> al llamar las APIs admin.",
    ],
    vars: [
      {
        name: "VITE_CLERK_PUBLISHABLE_KEY",
        example: "pk_live_...",
        description: "Clave publicable de Clerk para el frontend. Requerida para renderizar el botón de login.",
        required: "required",
        secret: false,
        scope: "client",
      },
      {
        name: "CLERK_SECRET_KEY",
        example: "sk_live_...",
        description: "Clave secreta de Clerk para el backend. Usada para verificar sesiones de usuarios admin.",
        required: "required",
        secret: true,
        scope: "server",
      },
      {
        name: "ADMIN_DASHBOARD_API_KEY",
        example: "una-clave-secreta-larga-y-aleatoria",
        description: "Clave arbitraria para proteger las APIs admin (/api/admin/*). Enviada como Authorization: Bearer <key>. Genera una clave fuerte aleatoria.",
        required: "required",
        secret: true,
        scope: "server",
      },
    ],
  },
  {
    title: "Base de Datos",
    icon: <Database className="h-4 w-4" />,
    color: "text-green-600",
    notes: [
      "Railway Postgres: en runtime el servicio web usa DATABASE_URL (red privada *.railway.internal).",
      "Para scripts locales o CI fuera de Railway: DATABASE_PUBLIC_URL (proxy TCP público).",
      "KV cache: KV_REST_API_URL + KV_REST_API_TOKEN (Upstash Redis REST en Railway).",
    ],
    vars: [
      {
        name: "DATABASE_URL",
        example: "postgresql://user:pass@postgres.railway.internal:5432/railway",
        description: "URL de Postgres en la red privada de Railway. Usada por anaafernandez-web y webhook al arrancar.",
        required: "required",
        secret: true,
        scope: "server",
      },
      {
        name: "KV_REST_API_URL",
        example: "https://http-production-xxxx.up.railway.app",
        description: "Endpoint REST de Upstash-compatible Redis (adaptador en Railway). Comparte cache entre réplicas.",
        required: "recommended",
        secret: false,
        scope: "server",
      },
      {
        name: "KV_REST_API_TOKEN",
        example: "SR_TOKEN…",
        description: "Token del adaptador Redis REST. Par con KV_REST_API_URL.",
        required: "recommended",
        secret: true,
        scope: "server",
      },
    ],
  },
  {
    title: "Webhooks — URLs a registrar externamente",
    icon: <Webhook className="h-4 w-4" />,
    color: "text-orange-600",
    notes: [
      "Estas URLs no son variables de entorno — son las URLs que debes registrar en los paneles externos.",
      "Reemplaza el dominio con el de tu brand.",
    ],
    vars: [
      {
        name: `https://${BRAND_SITE_HOST}/api/stripe-webhook`,
        description: "Registrar en: Stripe Dashboard → Webhooks → Add endpoint. Eventos mínimos: checkout.session.completed, customer.subscription.*, invoice.payment_*",
        required: "required",
        secret: false,
      },
      {
        name: `https://${BRAND_SITE_HOST}/webhook`,
        description: "Registrar en: MercadoPago Developers → Tu aplicación → Webhooks → Agregar webhook. Tipo: subscription_preapproval y payment. El servidor en Railway también acepta /webhook como alias de /api/webhook.",
        required: "required",
        secret: false,
      },
    ],
  },
  {
    title: "Precios de Membresía (env vars de precios)",
    icon: <CreditCard className="h-4 w-4" />,
    color: "text-amber-600",
    notes: [
      "Estos precios son el fallback cuando no hay un stripe_price_id configurado en la DB.",
      "Una vez que configures los planes en el dashboard (Planes de Suscripción), estos env vars dejan de ser críticos para Stripe.",
      "Para MercadoPago, el precio real lo define el preapproval_plan — estos vars son solo referencia visual en la UI.",
    ],
    vars: [
      {
        name: "VITE_PREMIUM_USD_STANDARD_PRICE",
        example: "29.99",
        description: "Precio Standard en USD. Fallback cuando no hay stripe_price_id en DB.",
        required: "recommended",
        secret: false,
        scope: "client",
      },
      {
        name: "VITE_PREMIUM_USD_VIP_PRICE",
        example: "49.99",
        description: "Precio VIP en USD. Fallback cuando no hay stripe_price_id en DB.",
        required: "recommended",
        secret: false,
        scope: "client",
      },
      {
        name: "VITE_PREMIUM_ARS_STANDARD_PRICE",
        example: "29999",
        description: "Precio Standard en ARS. Mostrado en la UI para Argentina.",
        required: "recommended",
        secret: false,
        scope: "client",
      },
      {
        name: "VITE_PREMIUM_ARS_VIP_PRICE",
        example: "49999",
        description: "Precio VIP en ARS. Mostrado en la UI para Argentina.",
        required: "recommended",
        secret: false,
        scope: "client",
      },
    ],
  },
  {
    title: "Redirects post-pago (fallback env vars)",
    icon: <ExternalLink className="h-4 w-4" />,
    color: "text-teal-600",
    notes: [
      "Estas son las URLs de fallback para cuando no hay success_redirect_url configurada en el dashboard.",
      "Lo correcto es configurarlas en: Dashboard → Planes de Suscripción → URL de éxito.",
      "Estos env vars solo se usan si el dashboard no tiene configuración.",
    ],
    vars: [
      {
        name: "VITE_SUCCESS_REDIRECT_STANDARD",
        example: "https://t.me/+XXXXXXXXXX",
        description: "URL de redirección tras pago exitoso para nivel Standard (y Basic). Típicamente el link de invitación al grupo de Telegram.",
        required: "recommended",
        secret: false,
        scope: "client",
      },
      {
        name: "VITE_SUCCESS_REDIRECT_VIP",
        example: "https://fan.tubrand.com/registro",
        description: "URL de redirección tras pago exitoso para nivel VIP. Típicamente el portal exclusivo del brand.",
        required: "recommended",
        secret: false,
        scope: "client",
      },
    ],
  },
];

// ── Env var row ────────────────────────────────────────────────────────────────

function VarRow({ v }: { v: EnvVar }) {
  const isWebhookUrl = v.name.startsWith("https://");
  return (
    <div className="py-3 border-b last:border-0 space-y-1">
      <div className="flex items-start gap-2 flex-wrap">
        <code className={`text-xs font-mono px-2 py-1 rounded font-semibold break-all ${isWebhookUrl ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" : "bg-muted text-foreground"}`}>
          {v.name}
        </code>
        {!isWebhookUrl && (
          <CopyButton text={v.name} />
        )}
        <div className="flex items-center gap-1.5 ml-auto shrink-0 flex-wrap">
          {v.secret && (
            <span className="flex items-center gap-0.5 text-xs text-red-600 dark:text-red-400">
              <Lock className="h-3 w-3" />
              secreto
            </span>
          )}
          {v.scope === "client" && (
            <span className="flex items-center gap-0.5 text-xs text-blue-600 dark:text-blue-400">
              <Eye className="h-3 w-3" />
              frontend
            </span>
          )}
          {v.scope === "server" && (
            <span className="flex items-center gap-0.5 text-xs text-purple-600 dark:text-purple-400">
              <Shield className="h-3 w-3" />
              server only
            </span>
          )}
          <Badge
            variant={v.required === "required" ? "destructive" : v.required === "recommended" ? "default" : "secondary"}
            className="text-xs py-0 h-5"
          >
            {v.required === "required" ? "requerida" : v.required === "recommended" ? "recomendada" : "opcional"}
          </Badge>
        </div>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{v.description}</p>
      {v.example && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs text-muted-foreground">Ejemplo:</span>
          <code className="text-xs bg-muted/60 px-1.5 py-0.5 rounded text-foreground/70 break-all">
            {v.secret ? v.example.slice(0, 12) + "..." : v.example}
          </code>
          {!v.secret && <CopyButton text={v.example} />}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function EnvSetup() {
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <AdminPageHeader
        description="Checklist Railway (web + webhook), build VITE_* y Worker Cloudflare. SPA Vite — sin Nuxt/Vercel."
        actions={
          <Button variant="outline" size="sm" asChild className="gap-1.5 shrink-0">
            <a href="https://railway.com/dashboard" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5" />
              Abrir Railway
            </a>
          </Button>
        }
      />

      {/* Warning banner */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 p-3 flex gap-2.5 text-xs text-amber-800 dark:text-amber-300">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold mb-0.5">Nunca pongas secretos directamente en el código o en .env commiteado.</p>
          <p>Todos los valores marcados como <Lock className="h-3 w-3 inline" /> <strong>secreto</strong> deben cargarse en{" "}
            <a href="https://railway.com/dashboard" target="_blank" rel="noopener noreferrer" className="underline">
              Railway → proyecto → Variables
            </a>{" "}
            en los servicios <code className="text-[10px] bg-muted px-1 rounded">anaafernandez-web</code> y{" "}
            <code className="text-[10px] bg-muted px-1 rounded">anaafernandez-webhook</code>.
            Las <code className="text-[10px] bg-muted px-1 rounded">VITE_*</code> van también en el build del servicio web.
          </p>
        </div>
      </div>

      {/* Stripe-only policy */}
      <div className="rounded-lg border border-indigo-200 bg-indigo-50 dark:bg-indigo-950/20 dark:border-indigo-900 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-indigo-600" />
          <p className="text-sm font-semibold text-indigo-800 dark:text-indigo-300">Pagos: Stripe únicamente (USD)</p>
        </div>
        <p className="text-xs text-indigo-700 dark:text-indigo-400">
          Argentina y el resto del mundo usan Stripe. No configures MercadoPago en Railway para este brand.
          Webhook Stripe: <code className="font-mono text-[11px]">{`https://${BRAND_SITE_HOST}/api/stripe-webhook`}</code>
        </p>
      </div>

      {/* Sections */}
      {SECTIONS.map((section) => (
        <div key={section.title} className="rounded-lg border overflow-hidden">
          {/* Section header */}
          <div className="flex items-center gap-2.5 px-4 py-3 bg-muted/40 border-b">
            <span className={section.color}>{section.icon}</span>
            <h2 className="text-sm font-semibold">{section.title}</h2>
            {section.docs && (
              <a
                href={section.docs.url}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {section.docs.label}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          {/* Notes */}
          {section.notes && (
            <div className="px-4 py-2.5 bg-muted/20 border-b space-y-1">
              {section.notes.map((note, i) => (
                <p key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  {note}
                </p>
              ))}
            </div>
          )}

          {/* Vars */}
          <div className="px-4 divide-y">
            {section.vars.map((v) => (
              <VarRow key={v.name} v={v} />
            ))}
          </div>
        </div>
      ))}

      {/* Setup order */}
      <div className="rounded-lg border p-4 space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          Orden recomendado de setup para un nuevo brand
        </h2>
        <Separator />
        <ol className="space-y-2.5 text-xs text-muted-foreground list-none">
          {[
            { step: "1", text: "Cargar variables en Railway (web + webhook). VITE_* en el servicio que hace el build." },
            { step: "2", text: "Registrar el webhook de Stripe en el dashboard de Stripe.", link: { label: "Stripe Webhooks", url: "https://dashboard.stripe.com/webhooks" } },
            { step: "3", text: "Push a main → Railway redeploy. Las migraciones de DB corren al arrancar (npm start)." },
            { step: "4", text: "Ir a Dashboard → Planes de Suscripción y crear los planes de Stripe para cada nivel." },
            { step: "5", text: "Configurar URLs de éxito post-pago (Telegram, portal VIP, etc.)." },
            { step: "6", text: "Probar checkout con tarjeta de test de Stripe (AR e internacional)." },
          ].map(({ step, text, link }) => (
            <li key={step} className="flex items-start gap-2.5">
              <span className="h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold shrink-0">
                {step}
              </span>
              <span className="leading-relaxed pt-0.5">
                {text}
                {link && (
                  <>
                    {" "}
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-foreground underline inline-flex items-center gap-0.5">
                      {link.label} <ExternalLink className="h-3 w-3" />
                    </a>
                  </>
                )}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
